import { DragEvent, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  AiCallEstimate,
  Document,
  DocUploadSession,
  DocUploadStatus,
} from "../lib/api";

interface Props {
  caseId: number;
  onUploaded: (docs: Document[]) => void;
  // Bumped by the parent to ask the dropzone to open its file picker (the
  // "+ Upload documents" shortcut). 0 means "never requested".
  openPickerRequest?: number;
}

/**
 * Document dropzone with two upload paths that converge on the same
 * `documents` row + ingest job:
 *
 *   * files smaller than DOC_LARGE_THRESHOLD_MB ride the original single
 *     multipart POST — completely unchanged behaviour.
 *   * files at/above the threshold use the chunked, resumable upload
 *     (open -> PUT chunks -> /complete), so multi-GB files never hit memory
 *     or a single nginx request. These land with ingest_profile='large' and
 *     run the streaming, budget-capped ingest on the server.
 *
 * Large files also surface a pre-upload AI-call/token estimate so the user
 * knows roughly how much AI work the ingest will spend before committing.
 */

// Keep in lock-step with engine settings.DOC_LARGE_THRESHOLD_MB. Purely a
// client-side routing decision; the server is authoritative on its own caps.
const DOC_LARGE_THRESHOLD_MB = 256;
const DOC_HARD_CAP_MB = 2048; // engine settings.DOC_UPLOAD_MAX_MB (2 GB)
const CHUNK_CONCURRENCY = 2;
const MAX_RETRIES_PER_CHUNK = 4;

// Keep in lock-step with engine documents._ALLOWED_EXT. The server is
// authoritative (rejects with 400), but filtering client-side gives instant
// feedback and keeps the native file picker scoped to supported types.
const ALLOWED_EXT = [
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".png",
  ".jpg",
  ".jpeg",
  ".tif",
  ".tiff",
];
const ACCEPT_ATTR = ALLOWED_EXT.join(",");
const SUPPORTED_LABEL = "PDF, DOCX, TXT, MD, or images (PNG, JPG, TIFF)";

function isSupported(file: File): boolean {
  const name = file.name.toLowerCase();
  return ALLOWED_EXT.some((ext) => name.endsWith(ext));
}

interface LargeActive {
  name: string;
  total: number;
  uploaded: number;
  status: "uploading" | "completing" | "complete" | "failed";
  error?: string;
  estimate?: AiCallEstimate;
}

function storageKey(caseId: number, file: File): string {
  return `cf_doc_upload:${caseId}:${file.name}:${file.size}:${file.lastModified}`;
}

export default function DocDropzone({ caseId, onUploaded, openPickerRequest = 0 }: Props) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [translate, setTranslate] = useState(false);
  const [large, setLarge] = useState<LargeActive | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const handledPickerRequest = useRef(0);

  useEffect(() => {
    if (openPickerRequest <= handledPickerRequest.current) return;
    handledPickerRequest.current = openPickerRequest;
    wrapRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    // Browsers only honour a programmatic click on a file input shortly
    // after a user gesture; if it's refused, the dropzone is still on
    // screen to click or drop onto.
    if (!busy && !large) inputRef.current?.click();
  }, [openPickerRequest, busy, large]);
  const abortRef = useRef<AbortController | null>(null);
  const translateRef = useRef(false);
  translateRef.current = translate;

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  async function sendSmall(files: File[]) {
    if (!files.length) return;
    setBusy(true);
    // Note: err is managed by send() so a type-rejection message isn't wiped
    // when a mixed batch (supported + unsupported) is dropped at once.
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("translate", translateRef.current ? "true" : "false");
    try {
      const resp = await api.upload<Document[]>(`/cases/${caseId}/documents`, form);
      onUploaded(resp);
    } catch (e: any) {
      setErr(e?.message ?? "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function uploadLarge(file: File) {
    if (file.size > DOC_HARD_CAP_MB * 1024 * 1024) {
      setLarge({
        name: file.name,
        total: file.size,
        uploaded: 0,
        status: "failed",
        error: `File exceeds the ${DOC_HARD_CAP_MB} MB cap`,
      });
      return;
    }
    const mime = file.type || "application/octet-stream";
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    const lsKey = storageKey(caseId, file);

    // ----- Try to resume a prior session for this exact file -----
    let session: DocUploadSession | null = null;
    let alreadyReceived = new Set<number>();
    let alreadyBytes = 0;
    const cachedId = localStorage.getItem(lsKey);
    if (cachedId) {
      try {
        const probed = await api.get<DocUploadStatus>(
          `/cases/${caseId}/documents/uploads/${cachedId}`,
        );
        if (probed.status === "open") {
          session = {
            upload_id: probed.upload_id,
            chunk_size: probed.chunk_size,
            expected_chunks: probed.expected_chunks,
            received_indices: probed.received_indices,
            received_bytes: probed.received_bytes,
            // Resumed sessions don't re-return the estimate; leave it off.
            estimate: undefined as unknown as AiCallEstimate,
          };
          alreadyReceived = new Set(probed.received_indices);
          alreadyBytes = probed.received_bytes;
        } else {
          localStorage.removeItem(lsKey);
        }
      } catch {
        localStorage.removeItem(lsKey);
      }
    }

    try {
      if (!session) {
        session = await api.post<DocUploadSession>(
          `/cases/${caseId}/documents/uploads`,
          {
            original_name: file.name,
            mime_type: mime,
            total_bytes: file.size,
            translate: translateRef.current,
          },
        );
        localStorage.setItem(lsKey, session.upload_id);
      }

      setLarge({
        name: file.name,
        total: file.size,
        uploaded: alreadyBytes,
        status: "uploading",
        estimate: session.estimate,
      });

      const indices: number[] = [];
      for (let i = 0; i < session.expected_chunks; i++) {
        if (!alreadyReceived.has(i)) indices.push(i);
      }

      const inflight = new Map<number, number>();
      let completedBytes = alreadyBytes;
      const updateProgress = () => {
        let sum = completedBytes;
        for (const n of inflight.values()) sum += n;
        setLarge((prev) =>
          prev && prev.status === "uploading"
            ? { ...prev, uploaded: Math.min(sum, file.size) }
            : prev,
        );
      };

      const sendChunk = async (idx: number) => {
        const start = idx * session!.chunk_size;
        const end = Math.min(start + session!.chunk_size, file.size);
        const blob = file.slice(start, end);
        let attempt = 0;
        for (;;) {
          try {
            inflight.set(idx, 0);
            await api.putBlob(
              `/cases/${caseId}/documents/uploads/${session!.upload_id}/chunks/${idx}`,
              blob,
              {
                signal,
                onProgress: (loaded) => {
                  inflight.set(idx, loaded);
                  updateProgress();
                },
              },
            );
            inflight.delete(idx);
            completedBytes += blob.size;
            updateProgress();
            return;
          } catch (e: any) {
            inflight.delete(idx);
            if (signal.aborted) throw new Error("aborted");
            attempt += 1;
            if (attempt > MAX_RETRIES_PER_CHUNK) {
              throw e instanceof Error ? e : new Error("Chunk upload failed");
            }
            const delay =
              Math.min(8000, 500 * Math.pow(2, attempt - 1)) +
              Math.floor(Math.random() * 250);
            await new Promise((r) => setTimeout(r, delay));
          }
        }
      };

      let nextIdx = 0;
      const workers: Promise<void>[] = [];
      for (let w = 0; w < CHUNK_CONCURRENCY; w++) {
        workers.push(
          (async () => {
            while (nextIdx < indices.length) {
              const i = indices[nextIdx++];
              await sendChunk(i);
            }
          })(),
        );
      }
      await Promise.all(workers);

      setLarge((prev) =>
        prev ? { ...prev, status: "completing", uploaded: file.size } : prev,
      );

      const doc = await api.post<Document>(
        `/cases/${caseId}/documents/uploads/${session.upload_id}/complete`,
        undefined,
      );
      localStorage.removeItem(lsKey);
      setLarge((prev) => (prev ? { ...prev, status: "complete" } : prev));
      onUploaded([doc]);
      setTimeout(() => setLarge(null), 2500);
    } catch (e: unknown) {
      if ((e as Error)?.message === "aborted" || signal.aborted) {
        setLarge(null);
        return;
      }
      const msg =
        e instanceof ApiError
          ? e.message
          : (e as Error)?.message ?? "Upload failed";
      setLarge((prev) => (prev ? { ...prev, status: "failed", error: msg } : prev));
    }
  }

  async function send(files: FileList | File[]) {
    const arr = Array.from(files);
    if (!arr.length) return;

    const rejected = arr.filter((f) => !isSupported(f));
    const accepted = arr.filter((f) => isSupported(f));
    if (rejected.length) {
      const names = rejected.map((f) => f.name).join(", ");
      setErr(
        `Unsupported file type: ${names}. Allowed: ${SUPPORTED_LABEL}.`,
      );
      if (inputRef.current) inputRef.current.value = "";
    } else {
      setErr(null);
    }
    if (!accepted.length) return;

    const threshold = DOC_LARGE_THRESHOLD_MB * 1024 * 1024;
    const small = accepted.filter((f) => f.size > 0 && f.size < threshold);
    const big = accepted.filter((f) => f.size >= threshold);

    if (small.length) await sendSmall(small);
    // Large files go one at a time so the progress bar + estimate stay legible.
    for (const f of big) {
      // eslint-disable-next-line no-await-in-loop
      await uploadLarge(f);
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  function onDrop(ev: DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setDragging(false);
    if (ev.dataTransfer.files) void send(ev.dataTransfer.files);
  }

  async function onCancelLarge() {
    abortRef.current?.abort();
    setLarge(null);
  }

  const pct =
    large && large.total > 0
      ? Math.min(100, Math.round((large.uploaded / large.total) * 100))
      : 0;

  return (
    <div className="dropzone-wrap" ref={wrapRef}>
      <div
        className={`dropzone ${dragging ? "dropzone--active" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !busy && !large && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-disabled={busy || !!large}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={ACCEPT_ATTR}
          onChange={(e) => e.target.files && send(e.target.files)}
        />
        {large ? (
          <div className="video-uploader__active">
            <div className="video-uploader__active-title">
              {large.status === "uploading" && `Uploading ${large.name}…`}
              {large.status === "completing" && "Finalizing…"}
              {large.status === "complete" && "Upload complete"}
              {large.status === "failed" && "Upload failed"}
            </div>
            <div
              className="video-uploader__bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="video-uploader__bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="video-uploader__active-meta">
              {formatBytes(large.uploaded)} / {formatBytes(large.total)} — {pct}%
            </div>
            {large.estimate && (
              <div className="dropzone__estimate">
                Estimated AI work: ~{formatNum(large.estimate.total_calls)} calls ·{" "}
                ~{formatNum(large.estimate.input_tokens)} input /{" "}
                ~{formatNum(large.estimate.output_tokens)} output tokens
                {large.estimate.capped && " (timeline capped)"}
              </div>
            )}
            {large.error && (
              <div className="alert alert--error" style={{ marginTop: 12 }}>{large.error}</div>
            )}
            {(large.status === "uploading" || large.status === "completing") && (
              <button
                type="button"
                className="btn btn--sm"
                style={{ marginTop: 12 }}
                onClick={(e) => { e.stopPropagation(); void onCancelLarge(); }}
              >
                Cancel
              </button>
            )}
            {large.status === "failed" && (
              <button
                type="button"
                className="btn btn--sm"
                style={{ marginTop: 12 }}
                onClick={(e) => { e.stopPropagation(); setLarge(null); }}
              >
                Dismiss
              </button>
            )}
          </div>
        ) : busy ? (
          <div>Uploading…</div>
        ) : (
          <>
            <div className="dropzone__title">Drop files here, or click to select</div>
            <div className="dropzone__sub">
              PDF, DOCX, TXT, or images (OCR will run automatically). Large files
              (over {DOC_LARGE_THRESHOLD_MB} MB) upload in resumable chunks. Max
              2 GB / 5,000 pages per file.
            </div>
          </>
        )}
        {err && <div className="alert alert--error" style={{ marginTop: 12 }}>{err}</div>}
      </div>

      <label className="dropzone__option" title="Detects the document's language and translates it to English with AI before indexing, so non-English files are searchable from English questions. The original file is always kept.">
        <input
          type="checkbox"
          checked={translate}
          disabled={busy || !!large}
          onChange={(e) => setTranslate(e.target.checked)}
        />
        <span>
          Translate to English with AI
          <span className="dropzone__option-sub">
            For Spanish, Vietnamese, or other non-English documents. English files are left as-is.
          </span>
        </span>
      </label>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${Math.round(n)}`;
}
